import type { CmpNewsletterFieldsFragment, CmpNewsletterRecipientGroupDataInput, CmpNewsletterRecipientPreviewRecipientFieldsFragment } from "../../../graphql/types";
import {
  EmailBuilderState,
  EmailTemplateKey,
  buildDefaultEmailBuilderState,
  cloneEmailBuilderState,
} from "./emailBuilderData";

export type EmailStatus =
  "draft" | "review" | "scheduled" | "sending" | "sent" | "canceled" | "failed";

export type NewsroomStatus = "notPublished" | "published" | "unpublished";

// Current template types plus the legacy bulletin value.
export type EmailType =
  | "newsletter"
  | "eBlast"
  | "advocacyUpdate"
  | "pressRelease"
  | "announcement"
  | "memberWelcome"
  | "fromTheDesk"
  | "customEmail"
  | "bulletin";

export const emailTypeForTemplate = (key: EmailTemplateKey): EmailType => {
  if (key === "eblast") return "eBlast";
  if (key === "eventAnnouncement") return "announcement";
  if (key === "mediaRelease") return "pressRelease";
  return key;
};

export type RecipientSourceId =
  | "activeMembers"
  | "activePrimaryContacts"
  | "allMembers"
  | "nonMemberSubscribers"
  | "newMembers"
  | "renewingMembers"
  | "prospectMembers"
  | "eventAttendees"
  | "pastEventAttendees"
  | "eblastSubscribers"
  | "directRecipients"
  | "selectedMembers"
  | "governanceGroups"
  | "membersOfferingPerks"
  | "pendingMembers"
  | "suspendedMembers"
  | "eventSponsors";

export const nonMemberSubscribersRecipientSourceLabel =
  "Non Member Subscribers (Including website subscribers)";

export type NewsletterMemberFilters = {
  membershipTypeIds: string[];
  employeeRangeIds: string[];
  categoryIds: string[];
  memberSinceRangeIds: string[];
  renewalRangeIds: string[];
};

export const buildDefaultNewsletterMemberFilters =
  (): NewsletterMemberFilters => ({
    membershipTypeIds: [],
    employeeRangeIds: [],
    categoryIds: [],
    memberSinceRangeIds: [],
    renewalRangeIds: [],
  });

// Step 3 template selection. "" = no template chosen yet (gallery phase);
// the 8 template keys and their seeded blocks live in emailBuilderData
// (emailTemplatePresets).
export type EmailTemplateSelection = EmailTemplateKey | "";

export type EmailAttachment = {
  id: string;
  name: string;
  fileType: string;
  sizeBytes: number;
  sizeLabel: string;
  storageKey?: string;
  eTag?: string;
};

export type EmailOutboundStatus =
  "sending" | "sent" | "failed" | "skipped" | "deliveryUnknown";

export type EmailOutboundRecord = {
  id: string;
  kind: string;
  status: EmailOutboundStatus;
  toEmail: string;
  intendedToEmail: string;
  recipientOverrideUsed: boolean;
  subject: string;
  errorMessage: string;
  attachmentCount: number;
  createdAt: string;
  sentAt: string;
  failedAt: string;
  openedAt: string;
  clickedAt: string;
};

export type EmailDeliverySummary = {
  totalOutboundEmailCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  intendedRecipientCount: number;
  actualRecipientCount: number;
  recipientOverrideUsed: boolean;
  latestOutboundEmailAt: string;
};

export type RecipientGroup = {
  id: string;
  name: string;
  description: string;
  count: number;
  source: string;
  isArchived?: boolean;
  selection?: CmpNewsletterRecipientGroupDataInput;
};

export type EmailAudienceMemberOption = {
  id: string;
  companyName: string;
  companyLogo?: string | null;
  categoryLabels?: string[] | null;
  lifecycleStatus: string;
  membershipTypeId: string;
  membershipTypeName: string;
  dateJoinedUtcMilli?: number | null;
  fullTimeEmployees?: number | null;
  partTimeEmployees?: number | null;
  renewalDateUtcMilli?: number | null;
  lastRenewedAtUtcMilli?: number | null;
  categoryId?: string[] | null;
};

export type EmailAdditionalRecipient = {
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string | null;
};

export type RecipientPreviewSummary = {
  recipients?: CmpNewsletterRecipientPreviewRecipientFieldsFragment[];
  totalCandidateCount: number;
  uniqueValidRecipientCount: number;
  invalidRecipientCount: number;
  skippedRecipientCount: number;
  recipientOverrideUsed: boolean;
  actualTestRecipientEmail: string;
  recipientRoutingMode: string;
  /** Sandbox chambers only (Monday 13390105883), e.g. "Sand Bay is in test mode: ...". */
  sandboxNotice?: string | null;
};

export type EmailFormState = {
  id?: string;
  isTestData?: boolean;
  status: EmailStatus;
  subject: string;
  preheader: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  emailType: EmailType;
  campaignName: string;
  campaignDescription: string;
  category: string;
  tags: string;
  recipientSourceIds: RecipientSourceId[];
  memberFilters: NewsletterMemberFilters;
  recipientGroupIds: string[];
  selectedMemberIds: string[];
  eventIds: string[];
  // Claude Design (16) "Sponsors" audience: its own Select Events list.
  sponsorEventIds: string[];
  criteriaIds: string[];
  governanceGroupIds: string[];
  additionalRecipients: EmailAdditionalRecipient[];
  directRecipients: string;
  excludedRecipientEmails: string[];
  templateId: EmailTemplateSelection;
  headerImageName: string;
  content: string;
  builderState: EmailBuilderState;
  attachments: EmailAttachment[];
  publishToNewsroom: boolean;
  newsroomStatus: NewsroomStatus;
  newsroomSlug: string;
  // Design (16) Newsroom Preview image (newsroom.featuredImageUrl).
  newsroomImageUrl: string;
  newsroomPublishedAtUtcMilli: number | null;
  newsroomUnpublishedAtUtcMilli: number | null;
  seoTitle: string;
  seoDescription: string;
  timeZone: string;
  scheduledDate: string;
  scheduledTime: string;
  reviewNote: string;
  reviewHistory?: CmpNewsletterFieldsFragment["reviewHistory"];
  estimatedRecipientCount: number;
  invalidRecipientCount: number;
  skippedRecipientCount: number;
  sentRecipientCount: number;
  failedRecipientCount: number;
  skippedDeliveryCount: number;
  openCount: number;
  clickCount: number;
  outboundEmails: EmailOutboundRecord[];
  deliverySummary: EmailDeliverySummary | null;
  senderReadinessLabel: string;
  senderReadinessDetail: string;
};

export type EmailCampaign = {
  id: string;
  slug: string;
  subject: string;
  emailType: EmailType;
  category: string;
  audienceLabel: string;
  tags: string[];
  status: EmailStatus;
  newsroomStatus: NewsroomStatus;
  scheduledAt?: string;
  sentAt?: string;
  publishedAt?: string;
  intendedRecipients: number;
  invalidRecipients: number;
  skippedCandidateRecipients: number;
  sentRecipients: number;
  skippedRecipients: number;
  failedRecipients: number;
  skippedDeliveries: number;
  openCount: number;
  clickCount: number;
  attachmentCount: number;
  updatedAt: string;
};

// Labels/order match the Step-1 "Choose the Type of Communication" cards
// verbatim; "bulletin" stays last as a legacy value for older records.
export const emailTypeOptions: Array<{ value: EmailType; label: string }> = [
  { value: "newsletter", label: "Newsletter" },
  { value: "eBlast", label: "E-Blast" },
  { value: "advocacyUpdate", label: "Advocacy Update" },
  { value: "pressRelease", label: "Media Release" },
  { value: "announcement", label: "Event Announcement" },
  { value: "memberWelcome", label: "Member Welcome" },
  { value: "fromTheDesk", label: "From the Desk" },
  { value: "customEmail", label: "Custom Email" },
  { value: "bulletin", label: "Chamber Bulletin" },
];

export const buildDefaultCreateEmailForm = ({
  chamberLogo,
  chamberName,
  senderEmail,
  replyToEmail,
}: {
  chamberLogo?: string | null;
  chamberName?: string;
  senderEmail?: string;
  replyToEmail?: string;
} = {}): EmailFormState => ({
  status: "draft",
  subject: "",
  preheader: "",
  fromName: chamberName || "Chamber of Commerce",
  fromEmail: senderEmail || "",
  replyToEmail: replyToEmail || senderEmail || "",
  emailType: "newsletter",
  campaignName: "",
  campaignDescription: "",
  category: "",
  tags: "",
  recipientSourceIds: [],
  memberFilters: buildDefaultNewsletterMemberFilters(),
  recipientGroupIds: [],
  selectedMemberIds: [],
  eventIds: [],
  sponsorEventIds: [],
  criteriaIds: [],
  governanceGroupIds: [],
  additionalRecipients: [],
  excludedRecipientEmails: [],
  directRecipients: "",
  // Empty until the user picks one of the 8 templates in the Step 3 gallery.
  templateId: "",
  headerImageName: "",
  content: "",
  builderState: {
    ...buildDefaultEmailBuilderState(chamberName),
    logoUrl: chamberLogo || "",
  },
  attachments: [],
  publishToNewsroom: false,
  newsroomStatus: "notPublished",
  newsroomSlug: "",
  newsroomImageUrl: "",
  newsroomPublishedAtUtcMilli: null,
  newsroomUnpublishedAtUtcMilli: null,
  seoTitle: "",
  seoDescription: "",
  timeZone: "America/Toronto",
  scheduledDate: "",
  scheduledTime: "10:00",
  reviewNote: "",
  estimatedRecipientCount: 0,
  invalidRecipientCount: 0,
  skippedRecipientCount: 0,
  sentRecipientCount: 0,
  failedRecipientCount: 0,
  skippedDeliveryCount: 0,
  openCount: 0,
  clickCount: 0,
  outboundEmails: [],
  deliverySummary: null,
  senderReadinessLabel: "Not checked yet",
  senderReadinessDetail: "Save the draft to check this sender email.",
});

export const cloneEmailForm = (form: EmailFormState): EmailFormState => ({
  ...form,
  recipientSourceIds: [...form.recipientSourceIds],
  memberFilters: {
    membershipTypeIds: [...form.memberFilters.membershipTypeIds],
    employeeRangeIds: [...form.memberFilters.employeeRangeIds],
    categoryIds: [...form.memberFilters.categoryIds],
    memberSinceRangeIds: [...form.memberFilters.memberSinceRangeIds],
    renewalRangeIds: [...form.memberFilters.renewalRangeIds],
  },
  recipientGroupIds: [...form.recipientGroupIds],
  eventIds: [...form.eventIds],
  sponsorEventIds: [...form.sponsorEventIds],
  criteriaIds: [...form.criteriaIds],
  excludedRecipientEmails: [...form.excludedRecipientEmails],
  selectedMemberIds: [...form.selectedMemberIds],
  governanceGroupIds: [...form.governanceGroupIds],
  additionalRecipients: form.additionalRecipients.map((recipient) => ({
    ...recipient,
  })),
  builderState: cloneEmailBuilderState(form.builderState),
  attachments: form.attachments.map((attachment) => ({ ...attachment })),
  outboundEmails: form.outboundEmails.map((outboundEmail) => ({
    ...outboundEmail,
  })),
  deliverySummary: form.deliverySummary ? { ...form.deliverySummary } : null,
});

export const getEmailTypeLabel = (emailType: EmailType) =>
  emailTypeOptions.find((option) => option.value === emailType)?.label ||
  emailType;

export const getStatusLabel = (status: EmailStatus) => {
  switch (status) {
    case "review":
      return "Ready for Review";
    case "scheduled":
      return "Scheduled";
    case "sending":
      return "Sending";
    case "sent":
      return "Sent";
    case "canceled":
      return "Canceled";
    case "failed":
      return "Failed";
    case "draft":
    default:
      return "Draft";
  }
};

// Manage Emails (Monday D12, decision F3): the design shows Scheduled, Sent
// and Draft. Ready for Review and Canceled show as Draft; Sending and Failed
// stay visible so no row shows a false state.
export type ManageEmailStatusGroup = "Scheduled" | "Sent" | "Draft" | "Sending" | "Failed";
export const manageEmailStatusGroups: ManageEmailStatusGroup[] = ["Scheduled", "Sent", "Draft", "Sending", "Failed"];
export const getManageEmailStatusGroup = (status: EmailStatus): ManageEmailStatusGroup => {
  switch (status) {
    case "scheduled": return "Scheduled";
    case "sent": return "Sent";
    case "sending": return "Sending";
    case "failed": return "Failed";
    default: return "Draft";
  }
};

export const getNewsroomStatusLabel = (status: NewsroomStatus) => {
  switch (status) {
    case "published":
      return "Published";
    case "unpublished":
      return "Unpublished";
    case "notPublished":
    default:
      return "Not Published";
  }
};
